import { useClientPortalAPI, useFormikSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Divider,
  LinkButton,
  PrimaryButton,
  Radio,
  RadioGroupFormikField,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import { Schemas } from "../../../types";
import { AtsIntegrationHeader } from "../AtsIntegrationHeader";
import constants from "../../../../constants";
import { UNIFIED_WORKSPACE_ID } from "../../../../constants";

type Integration = {
  type: string;
  logo_url: string;
  name: string;
  description: string;
};

interface Props {
  initialValues: components["schemas"]["AtsIntegrationList"];
  reload: () => void;
}

export function UnifiedIntegration({ initialValues, reload }: Props) {
  const { "*": integrationType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const env = constants.MODE === "production" ? "Production" : "Staging";
  const { data: unifiedIntegrations } = useSWR<Integration[]>(
    `https://api.unified.to/unified/integration/workspace/${UNIFIED_WORKSPACE_ID}?env=${env}`,
    (url: string) => fetch(url).then((res) => res.json())
  );
  const integrationName =
    unifiedIntegrations?.find((each) => each.type === integrationType)?.name ||
    "";

  const { data: packages, isLoading: isLoadingPackages } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const { data: triggerStages, isLoading: isLoadingTriggerStages } =
    useClientPortalAPI(
      `/api/internal/client-portal/ats-integrations/${initialValues.id}/trigger-stages/`
    );

  const { submit, isSubmitting } = useFormikSubmit<
    Schemas["PublicAPIKeyCreateResponse"]
  >(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/update/`,
    {
      onSuccess: () => {
        showToast(t("Integration updated successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  return (
    <>
      <Stack gap="space-2" className={styles.stack}>
        <Text style="global/heading-large">{t("Integration details")}</Text>
        <Formik initialValues={initialValues} onSubmit={submit}>
          <Form>
            <AtsIntegrationHeader
              integrationName={integrationName}
              initialValues={initialValues}
              reload={reload}
            />
            <Card type="large" className={styles.card}>
              <Stack gap="space-4">
                <TextField
                  className={styles.textFieldSpacing}
                  testID="connection-id"
                  label={t("Connection ID")}
                  value={initialValues.unified_integration?.connection_id}
                  isDisabled
                  isRequired
                />
                <Divider />
                <RadioGroupFormikField
                  label={t("Report URL type")}
                  testID="report-url-type"
                  name="report_url_type"
                  orientation="horizontal"
                  isRequired
                  isDisabled={!initialValues.is_active}
                  defaultValue="DIRECT_PDF_URL"
                >
                  <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                    {t("PDF URL")}
                  </Radio>
                  <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                    {t("Client Portal URL (Authenticated)")}
                  </Radio>
                </RadioGroupFormikField>
                <Divider />
                <section>
                  <SelectFormikField
                    name="unified_integration.trigger_stage"
                    label={t("Trigger stage")}
                    placeholder={t("Please select")}
                    isDisabled={!initialValues.is_active}
                    isRequired
                    isLoading={isLoadingTriggerStages}
                    testID="ats-integration-trigger-stage-select"
                  >
                    {triggerStages?.map(({ id, text }) => (
                      <SelectItem key={id} id={id}>
                        {text}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <SelectFormikField
                  label={t("Default package")}
                  testID="default-package"
                  name="unified_integration.default_package_id"
                  isDisabled={!initialValues.is_active}
                  isRequired
                  isLoading={isLoadingPackages}
                >
                  {packages?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                <LinkButton
                  onPress={() => window.open("/packages/create/")}
                  testID="unified-create-package-link"
                >
                  {t("Create a new package")}
                </LinkButton>
                <SelectFormikField
                  label={t("Customer group")}
                  testID="customer-group"
                  name="unified_integration.customer_group_id"
                  isDisabled={!initialValues.is_active}
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                <LinkButton
                  onPress={() => window.open("/groups/create/")}
                  testID="unified-create-groups-link"
                >
                  {t("Create a new group")}
                </LinkButton>
                <Stack orientation="horizontal" justify="end" gap="space-4">
                  <BasicButton
                    testID="cancel-unified"
                    type="button"
                    isDisabled={isSubmitting}
                    onPress={() => navigate("/settings/ats-integrations")}
                  >
                    {t("Cancel")}
                  </BasicButton>
                  <PrimaryButton
                    testID="save-unified"
                    type="submit"
                    isDisabled={!initialValues.is_active || isSubmitting}
                  >
                    {t("Save changes")}
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Card>
          </Form>
        </Formik>
      </Stack>
    </>
  );
}

const styles = {
  textFieldSpacing: css({ display: "flex", flex: "1" }),
  stack: css({
    gap: "var(--space-4)",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
};
